import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

/** One Brewfather device reading (times are epoch ms, sg specific gravity, temp °C). */
export interface IBfReading {
  sg?: number;
  temp?: number;
  time: number;
}

/**
 * Tiny inline SG/temp chart for the fermentation progress: SG curve with an
 * OG label, a dashed FG-target line and a muted temperature curve. Pure
 * presentational — everything is precomputed to SVG coordinates in ngOnChanges.
 */
@Component({
  selector: 'app-fermentation-chart',
  templateUrl: './fermentation-chart.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  styles: [':host{display:block;width:100%} svg{display:block;width:100%;height:auto}'],
})
export class FermentationChartComponent implements OnChanges {

  @Input() readings: IBfReading[] = [];
  @Input() og?: number | null;
  @Input() fg?: number | null;

  hasData = false;
  sgPoints = '';
  tempPoints = '';
  fgY: number | null = null;
  lastX = 0;
  lastY = 0;
  ogLabel = '';
  fgLabel = '';
  sgLabel = '';
  startLabel = '';
  endLabel = '';

  // Plot area inside the 300x130 viewBox.
  private readonly left = 8;
  private readonly right = 292;
  private readonly top = 16;
  private readonly bottom = 110;

  ngOnChanges(): void {
    const sorted = [...(this.readings ?? [])]
      .filter(r => r && r.time != null)
      .sort((a, b) => a.time - b.time);
    const sgReadings = sorted.filter(r => r.sg != null);

    this.hasData = sgReadings.length > 1;
    if (!this.hasData) return;

    const t0 = sorted[0].time;
    const t1 = sorted[sorted.length - 1].time;
    const x = (t: number) => t1 === t0
      ? (this.left + this.right) / 2
      : this.left + ((t - t0) / (t1 - t0)) * (this.right - this.left);

    const sgValues = sgReadings.map(r => r.sg!);
    let lo = Math.min(...sgValues, this.fg ?? Number.POSITIVE_INFINITY);
    let hi = Math.max(...sgValues, this.og ?? Number.NEGATIVE_INFINITY);
    lo -= 0.002;
    hi += 0.002;
    const ySg = (v: number) => this.top + ((hi - v) / (hi - lo)) * (this.bottom - this.top);

    this.sgPoints = sgReadings.map(r => `${x(r.time).toFixed(1)},${ySg(r.sg!).toFixed(1)}`).join(' ');
    const last = sgReadings[sgReadings.length - 1];
    this.lastX = +x(last.time).toFixed(1);
    this.lastY = +ySg(last.sg!).toFixed(1);

    this.fgY = this.fg != null ? +ySg(this.fg).toFixed(1) : null;
    this.ogLabel = this.og != null ? this.og.toFixed(3) : '';
    this.fgLabel = this.fg != null ? this.fg.toFixed(3) : '';
    this.sgLabel = last.sg!.toFixed(3);
    this.startLabel = this.formatDate(t0);
    this.endLabel = this.formatDate(t1);

    const tempReadings = sorted.filter(r => r.temp != null);
    if (tempReadings.length > 1) {
      const temps = tempReadings.map(r => r.temp!);
      const tLo = Math.min(...temps) - 1;
      const tHi = Math.max(...temps) + 1;
      const yTemp = (v: number) => this.top + ((tHi - v) / (tHi - tLo)) * (this.bottom - this.top);
      this.tempPoints = tempReadings.map(r => `${x(r.time).toFixed(1)},${yTemp(r.temp!).toFixed(1)}`).join(' ');
    } else {
      this.tempPoints = '';
    }
  }

  private formatDate(time: number): string {
    const date = new Date(time);
    return `${date.getDate()}.${date.getMonth() + 1}`;
  }
}
